import {
  CredentialsSchema,
  GitIntegrationSchema,
  RepositorySchema,
} from '@terra/shared-schemas-git';
import {
  CrawlerSessionSchema as GeneratedCrawlerSessionSchema,
  CrawlerTypeSchema,
} from '@terra/shared-schemas-headless-browser';
import {
  ApplicationBusinessUnitSchema,
  ApplicationSchema,
  ApplicationScopingSchema,
  BrowserMatchReplaceRuleWithRelationsSchema,
  BusinessUnitSchema,
  CvssEnvironmentalRequirementsSchema,
  CvssVersionSettingSchema,
  FileScopingDataSchema,
  GuardrailActionSchema,
  GuardrailCategorySchema,
  GuardrailPipelineSchema,
  HttpInterceptionRuleWithRelationsSchema,
  InstructionCategorySchema,
  KeyValueSchema,
  SignalCategorySchema,
  SignalStageSchema,
  TechnologyStackSchema,
  TestingInstructionApplyModeSchema,
  TestingInstructionPipelineSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  LoginBehaviorProfileSchema,
  LoginBehaviorUpdateSchema,
} from './login-behavior/application-login-behavior.schema';
import { uploadedFilesSchema } from '@terra/shared-authorization';
import { isValidYearlyMonthDay } from '@terra/shared-utils';
import { z } from 'zod';
import { DomainConfigReturnSchema } from './domain-config.schema';

export const ApplicationBusinessUnitOnApplicationSchema =
  ApplicationBusinessUnitSchema.extend({
    businessUnit: BusinessUnitSchema,
  });

export const GuardrailRuleSchema = z.object({
  id: z.string(),
  rule: z.string(),
});

export const GuardrailValidationErrorSchema = z.object({
  field: z.string(),
  fieldType: z.enum(['description', 'rule']),
  ruleIndex: z.number().optional(),
  issues: z.array(z.string()),
  originalContent: z.string(),
});

export const NoteValidationErrorSchema = z.object({
  field: z.string(),
  fieldType: z.literal('content'),
  issues: z.array(z.string()),
  originalContent: z.string(),
});

export const ApplicationGuardrailSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  applicationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: GuardrailCategorySchema,
  enabled: z.boolean().default(true),
  rules: z.array(GuardrailRuleSchema),
  action: GuardrailActionSchema.default('WARN'),
  pipelines: GuardrailPipelineSchema.array().default([]),
  isDefault: z.boolean().default(false),
  triggerCount: z.number().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const ApplicationNoteSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  applicationId: z.string(),
  title: z.string(),
  content: z.string(),
  category: InstructionCategorySchema.nullable(),
  applyMode: TestingInstructionApplyModeSchema.default('ALWAYS'),
  pipelines: TestingInstructionPipelineSchema.array().default([]),
  endpointGlobs: z.array(z.string()).default([]),
  description: z.string().nullable().default(null),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

import {
  parseJsonIfString,
  processDomainConfigList,
  processFormValue,
  processRepositoryList,
} from './utils/form.utils';
import { DomainConfigListSchema } from './domain-config.schema';
import { isValidApplicationUrl, isValidDomainHost } from './domain-validation';
import { refineBuiltApplicationDomains } from './validate-built-application-domains.util';

const applicationUrlHostnameIsValid = (url: string): boolean => {
  const trimmed = url.trim();
  const normalized = trimmed.startsWith('http')
    ? trimmed
    : `https://${trimmed}`;

  try {
    return isValidDomainHost(new URL(normalized).hostname);
  } catch {
    return false;
  }
};

const applicationUrlSchema = z
  .string()
  .min(1)
  .refine(isValidApplicationUrl, {
    error: 'Application URL must be a valid http or https URL.',
  })
  .refine(applicationUrlHostnameIsValid, {
    error: 'Application URL must use a valid hostname.',
  });

export const ApplicationsIdSchema = ApplicationSchema.pick({
  id: true,
});

export const ApplicationIntentSchema = z.enum(['onboard', 'add']);

export const AutomationScheduleSchema = z
  .object({
    daysOfWeek: z
      .array(z.number().min(0).max(6))
      .min(1, 'At least one day must be selected'),
    startHour: z.number().min(0).max(23),
    endHour: z.number().min(0).max(24),
    timezone: z.string().optional().nullable(),
  })
  .refine((data) => data.endHour !== data.startHour, {
    path: ['endHour'],
    error: 'End hour must differ from start hour',
  });

export const CrawlerPeriodicSettingsSchema = z
  .object({
    enabled: z.boolean(),
    windowEnabled: z.boolean(),
    dayOfWeek: z.number().int().min(0).max(6).nullable(),
    startHour: z.number().int().min(0).max(23).nullable(),
    timezone: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.enabled || !data.windowEnabled) {
      return;
    }

    if (data.dayOfWeek === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['dayOfWeek'],
        message: 'Day of week is required when crawl window is enabled',
      });
    }

    if (data.startHour === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['startHour'],
        message: 'Start hour is required when crawl window is enabled',
      });
    }

    if (!data.timezone?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['timezone'],
        message: 'Timezone is required when crawl window is enabled',
      });
    }
  })
  .transform((data) => {
    if (!data.enabled) {
      return {
        enabled: false,
        windowEnabled: false,
        dayOfWeek: null,
        startHour: null,
        timezone: null,
      };
    }

    if (!data.windowEnabled) {
      return {
        enabled: true,
        windowEnabled: false,
        dayOfWeek: null,
        startHour: null,
        timezone: null,
      };
    }

    return {
      enabled: true,
      windowEnabled: true,
      dayOfWeek: data.dayOfWeek,
      startHour: data.startHour,
      timezone: data.timezone,
    };
  });

export type CrawlerPeriodicSettingsDto = z.infer<
  typeof CrawlerPeriodicSettingsSchema
>;

export const DEFAULT_CRAWLER_PERIODIC_SETTINGS = {
  enabled: false,
  windowEnabled: false,
  dayOfWeek: null,
  startHour: null,
  timezone: null,
} as const satisfies CrawlerPeriodicSettingsDto;

export const ScheduledReportFrequencySchema = z.enum([
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
  'YEARLY',
]);

export const ScheduledReportRuleSchema = z
  .object({
    id: z.string().min(1),
    frequency: ScheduledReportFrequencySchema,
    daysOfWeek: z.array(z.number().int().min(0).max(6)).default([]),
    dayOfMonth: z.number().int().min(1).max(28).optional().nullable(),
    lastDayOfMonth: z.boolean().optional().nullable(),
    fiscalYearStartMonth: z.number().int().min(1).max(12).optional().nullable(),
    yearlyMonth: z.number().int().min(1).max(12).optional().nullable(),
    yearlyDay: z.number().int().min(1).max(31).optional().nullable(),
    hour: z.number().int().min(0).max(23),
    timezone: z.string().min(1),
    lastRunAt: z.coerce.date().optional().nullable(),
    nextRunAt: z.coerce.date().optional().nullable(),
  })
  .superRefine((rule, ctx) => {
    if (rule.frequency === 'WEEKLY' && rule.daysOfWeek.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one day of the week must be selected',
        path: ['daysOfWeek'],
      });
    }

    if (rule.frequency === 'MONTHLY') {
      const hasDayOfMonth =
        rule.dayOfMonth !== undefined && rule.dayOfMonth !== null;
      const hasLastDay = rule.lastDayOfMonth === true;

      if (!hasDayOfMonth && !hasLastDay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select a day of month or last day of month',
          path: ['dayOfMonth'],
        });
      }
    }

    if (
      (rule.frequency === 'QUARTERLY' || rule.frequency === 'SEMI_ANNUAL') &&
      (rule.fiscalYearStartMonth === undefined ||
        rule.fiscalYearStartMonth === null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          rule.frequency === 'SEMI_ANNUAL'
            ? 'Starting month is required'
            : 'Fiscal year start month is required',
        path: ['fiscalYearStartMonth'],
      });
    }

    if (rule.frequency === 'YEARLY') {
      if (rule.yearlyMonth === undefined || rule.yearlyMonth === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Yearly month is required',
          path: ['yearlyMonth'],
        });
      }

      if (rule.yearlyDay === undefined || rule.yearlyDay === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Yearly day is required',
          path: ['yearlyDay'],
        });
      } else if (
        rule.yearlyMonth !== undefined &&
        rule.yearlyMonth !== null &&
        !isValidYearlyMonthDay({
          yearlyMonth: rule.yearlyMonth,
          yearlyDay: rule.yearlyDay,
        })
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Yearly day is not valid for the selected month',
          path: ['yearlyDay'],
        });
      }
    }
  });

export const ScheduledReportsConfigSchema = z
  .object({
    enabled: z.boolean(),
    rules: z.array(ScheduledReportRuleSchema).default([]),
  })
  .superRefine((config, ctx) => {
    if (config.enabled && config.rules.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one schedule rule is required when enabled',
        path: ['rules'],
      });
    }
  });

const gitHubAppKindSchema = GitIntegrationSchema.shape.gitHubAppKind;

function withDefaultedApplyTo(rules: unknown): unknown {
  if (!Array.isArray(rules)) {
    return rules;
  }

  return rules.map((rule) =>
    rule &&
    typeof rule === 'object' &&
    (rule as { applyTo?: unknown }).applyTo == null
      ? { ...rule, applyTo: [] }
      : rule,
  );
}

export const ApplicationBrowserConfigBodySchema = z.strictObject({
  httpInterceptionRules: z
    .lazy(() => HttpInterceptionRuleWithRelationsSchema)
    .array()
    .optional(),
  matchReplaceRules: z
    .preprocess(
      withDefaultedApplyTo,
      z
        .lazy(() => BrowserMatchReplaceRuleWithRelationsSchema)
        .array()
        .optional(),
    )
    .optional(),
});

export const DiscoveryContentTypeOriginSchema = z.enum(['DEFAULT', 'CUSTOM']);

export const DiscoveryContentTypeConfigSchema = z.object({
  mimeType: z.string().min(1),
  description: z.string().nullish(),
  origin: DiscoveryContentTypeOriginSchema,
  crawlerEnabled: z.boolean(),
  torchTrackingEnabled: z.boolean(),
});

const applicationsBodyParsedSchema = z.preprocess(
  (formData) => {
    if (typeof formData !== 'object' || formData === null) {
      return formData;
    }

    return Object.entries(formData as Record<string, unknown>).reduce(
      (processedData, [key, value]) => ({
        ...processedData,
        [key]: (() => {
          switch (key) {
            case 'repositories':
              return processRepositoryList(value);
            case 'credentials':
              return parseJsonIfString(value);
            case 'domains':
              return processDomainConfigList(value);
            default:
              return processFormValue(value, key);
          }
        })(),
      }),
      {
        domains: [],
      },
    );
  },
  z.intersection(
    ApplicationSchema.omit({
      id: true,
      tenantId: true,
      owner: true,
      nextPentestReportReminderSentAt: true,
      lastWeeklyCrawlAt: true,
      anonymousKatanaDispatchedAt: true,
      kind: true,
      isDraft: true,
      sourceSurfaceId: true,
      createdAt: true,
      updatedAt: true,
      isEnabled: true,
      scheduledReportsEnabled: true,
      dynamicApplicationScanEnabled: true,
      dynamicApplicationScanCredentialId: true,
      dynamicApplicationScanVulnerabilityEnabled: true,
      dynamicApplicationScanDirectoryEnabled: true,
      targetRequestsPerMinute: true,
      scanEffortLevel: true,
      cvssVersion: true,
      tailscaleNetworkId: true,
      signalImportHostAliases: true,
      signalImportMountMappings: true,
    }).extend({
      url: applicationUrlSchema,
      redirectToUrl: applicationUrlSchema.optional().nullable(),
      signalCategoriesInScope: SignalCategorySchema.array().optional(),
      documentationUrls: z.array(z.url()).optional(),
      waitingHoursForDeployment: z.int().min(12).optional(),
      applyDeploymentDelayToTicketingRetests: z.boolean().optional(),
      internalAccessNetworkId: z.string().nullable().optional(),
      limitGlobalHeadersByScope: z.boolean().nullable().optional(),
      crawlerPeriodic: CrawlerPeriodicSettingsSchema.optional().nullable(),
    }),
    z.intersection(
      GitIntegrationSchema.extend({
        credentials: CredentialsSchema.omit({
          personalTokenEnc: true,
          gitHubAppId: true,
          gitHubAppPrivateKeyEnc: true,
          gitLabUserCredentialId: true,
        })
          .extend({
            personalToken: z.string().optional().nullable(),
            gitHubAppId: z.string().optional().nullable(),
            gitHubAppPrivateKey: z.string().optional().nullable(),
            gitlabAuthCode: z.string().optional().nullable(),
            azureDevOpsAuthCode: z.string().optional().nullable(),
          })
          .optional(),
        checkPrIssuesAfterHours: z.int().optional(),
        gitHubAppKind: gitHubAppKindSchema.nullable().optional(),
      }).pick({
        credentials: true,
        platform: true,
        checkPrIssuesAfterHours: true,
        baseUrl: true,
        apiVersion: true,
        gitHubAppKind: true,
      }),
      z.object({
        repositories: RepositorySchema.array().optional(),
        files: uploadedFilesSchema().optional(),
        domains: DomainConfigListSchema.default([]),
        intent: ApplicationIntentSchema,
        tenantIntegrationId: z.string().nullable().optional(),
        webhookSecret: z.string().nullable().optional(),
      }),
    ),
  ),
);

export const ApplicationsBodySchema = applicationsBodyParsedSchema.superRefine(
  (data, ctx) => {
    refineBuiltApplicationDomains({
      applicationUrl: data.url,
      domains: data.domains,
      ctx,
    });
  },
);

export const applicationUpdateBodySchema = z
  .object({
    name: z.string().optional(),
    url: applicationUrlSchema.optional(),
    redirectToUrl: applicationUrlSchema.optional().nullable(),
    documentationUrls: z.array(z.url()).optional(),
    environment: z.string().optional(),
    hasWaf: z.boolean().optional().nullable(),
    domains: DomainConfigListSchema.optional(),
    isInternalApp: z.boolean().optional().nullable(),
    isBackendOnly: z.boolean().optional().nullable(),
    smokeTestEnabled: z.boolean().optional().nullable(),
    crawlerPeriodic: CrawlerPeriodicSettingsSchema.optional().nullable(),
    signalCategoriesInScope: z.array(SignalCategorySchema).optional(),
    automationSchedule: AutomationScheduleSchema.optional().nullable(),
    supportsParallelExecution: z.boolean().optional().nullable(),
    loginBehavior: LoginBehaviorUpdateSchema.optional(),
    nextPentestReportDate: z.coerce.date().optional().nullable(),
    scheduledReportsEnabled: z.boolean().optional(),
    scheduledReportRules: z.array(ScheduledReportRuleSchema).optional(),
    globalHeaders: z.array(KeyValueSchema).max(20).optional(),
    limitGlobalHeadersByScope: z.boolean().nullable().optional(),
    browserConfig: ApplicationBrowserConfigBodySchema.optional().nullable(),
    discoveryContentTypes: z.array(DiscoveryContentTypeConfigSchema).optional(),
    waitingHoursForDeployment: z.int().min(12).optional(),
    applyDeploymentDelayToTicketingRetests: z.boolean().optional(),
    dynamicApplicationScanEnabled: z.boolean().optional(),
    dynamicApplicationScanScheduleRule:
      ScheduledReportRuleSchema.optional().nullable(),
    dynamicApplicationScanCredentialId: z.string().optional().nullable(),
    dynamicApplicationScanVulnerabilityEnabled: z.boolean().optional(),
    dynamicApplicationScanDirectoryEnabled: z.boolean().optional(),
    targetRequestsPerMinute: z
      .number()
      .int()
      .min(1)
      .max(6000)
      .optional()
      .nullable(),
    cvssVersion: CvssVersionSettingSchema.optional(),
    cvssEnvironmentalRequirements:
      CvssEnvironmentalRequirementsSchema.optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.url === undefined) {
      return;
    }

    refineBuiltApplicationDomains({
      applicationUrl: data.url,
      domains: data.domains ?? [],
      ctx,
    });
  });

export const ApplicationReturnSchema = ApplicationSchema.omit({
  tailscaleNetworkId: true,
}).extend(
  z.object({
    internalAccessNetworkId: z.string().nullable().optional(),
    domains: z.array(DomainConfigReturnSchema).default([]),
    documentationUrls: z.array(z.url()).default([]),
    businessUnits: z
      .array(ApplicationBusinessUnitOnApplicationSchema)
      .default([]),
    filesUploadedCount: z.int().min(0).optional(),
    browserConfig: ApplicationBrowserConfigBodySchema.nullable().optional(),
    discoveryContentTypes: z
      .array(DiscoveryContentTypeConfigSchema)
      .optional()
      .default([]),
    automationSchedule: AutomationScheduleSchema.nullable().optional(),
    crawlerPeriodic: CrawlerPeriodicSettingsSchema.nullable().optional(),
    loginBehavior: LoginBehaviorProfileSchema.nullable().optional(),
    scheduledReportRules: z.array(ScheduledReportRuleSchema).default([]),
    nextScheduledReportAt: z.coerce.date().nullable().optional(),
    activeScheduledReportRuleCount: z.number().int().min(0).optional(),
    cvssEnvironmentalRequirements:
      CvssEnvironmentalRequirementsSchema.nullable().optional(),
    limitGlobalHeadersByScope: z.boolean().nullable().optional(),
    aiKind: z.enum(['AGENT', 'MCP_SERVER']).optional(),
    mcpTransport: z.enum(['STDIO', 'SSE', 'HTTP']).nullable().optional(),
  }).shape,
);

export const ApplicationDetailReturnSchema = ApplicationReturnSchema.extend({
  globalHeaders: z.array(KeyValueSchema).max(20).default([]),
  limitGlobalHeadersByScope: z.boolean().nullable().optional(),
});

export const ApplicationsReturnSchema =
  ApplicationReturnSchema.array().describe('Applications');

export const ApplicationFilesSchema = z.object({
  files: uploadedFilesSchema(),
  fileType: z.enum(['DOCS', 'SWAGGER', 'TESTING_REPORTS', 'PROTO']).optional(),
});

export const ApplicationScopingReturnSchema = ApplicationScopingSchema.extend({
  fileScopingData: FileScopingDataSchema.nullable(),
  techStack: z
    .array(
      TechnologyStackSchema.extend({
        source: z.string().nullable().optional(),
        endpointIds: z.array(z.string()).catch([]).default([]),
      }),
    )
    .default([]),
}).nullable();

export const ApplicationScopingBodySchema = z.object({
  files: uploadedFilesSchema(),
});

export const ApplicationDiscoveryReturnSchema = z.object({
  message: z.string(),
});

export const TriggerRepositoryProfilingBodySchema = z.object({
  repositoryId: z.string().min(1).optional(),
  force: z.boolean().optional(),
});

export const DomainEnumerationBodySchema = z.object({
  domain: z
    .string()
    .min(1)
    .regex(
      /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
      'Must be a valid hostname',
    ),
});

export const DomainEnumerationReturnSchema = z.object({
  subdomains: z.array(z.string()),
});

export const ApplicationRepositoriesReturnSchema = z.object({
  repositories: RepositorySchema.array(),
});

export const ApplicationIndexRepositorySchema = ApplicationSchema.pick({
  id: true,
});

export const ApplicationIndexRepositoryBodySchema = z.object({
  repositoryId: z.int(),
});

export const ApplicationIndexRepositoryReturnSchema = z.object({
  message: z.string(),
});

export const ApplicationDeleteSchema = z.object({
  message: z.string(),
});

export const ApplicationGitIntegrationReturnSchema = z.object({
  message: z.string(),
});

export const ApplicationsCountReturnSchema = z.object({
  count: z.number(),
});

export const ApplicationCrawlerReturnSchema = z.object({
  message: z.string(),
});

export const ApplicationAssetsCountSchema = z.object({
  count: z.number(),
  endpoints: z.number(),
  webPages: z.number(),
});

export const ApplicationUrlEditRestrictionSchema = z.object({
  canEditUrl: z.boolean(),
  reason: z.string().optional(),
  endpointsCount: z.number(),
  signalsCount: z.number(),
});

export const ApplicationDashboardMetricsSchema = z.object({
  totalApplications: z.number(),
  qualitySignals: z.number(),
  apiCoverage: z.number(),
  securityScore: z.number(),
});

export const MetricValueSchema = z
  .number()
  .describe(
    'Metric count or score for the requested scope. When from and/or to are set, the value is limited to that window; omit from/to for current posture.',
  );

export const CountMetricSchema = z
  .object({
    total: MetricValueSchema.describe('Total count for this metric group'),
  })
  .describe(
    'Count-style metric. Posture when from/to are omitted; windowed when from and/or to are set.',
  );

export const PrTrendsDataPointSchema = z.object({
  date: z.coerce.date(),
  totalPrsCount: z.number(),
  reachablePrsCount: z.number(),
});

export const PrTrendsResponseSchema = z.object({
  data: PrTrendsDataPointSchema.array(),
});

export const SignalsTrendsDataPointSchema = z.object({
  date: z.coerce.date(),
  newCount: z.number(),
  inTestingCount: z.number(),
  needsReviewCount: z.number(),
  testedCount: z.number(),
});

export const SignalsTrendsResponseSchema = z.object({
  data: SignalsTrendsDataPointSchema.array(),
});

export const CveAggregationResultSchema = z.array(
  z.object({ _id: z.string() }),
);

export const SignalTrendAggregationResultSchema = z.array(
  z.object({
    _id: z.object({
      date: z.string(),
      stage: SignalStageSchema,
    }),
    count: z.number(),
  }),
);

export const ApplicationHealthMatrixSchema = z.object({
  applicationId: z.string(),
  applicationName: z.string(),
  businessUnit: z.string().optional(),
  findingsCount: z.number(),
  criticalFindings: z.number(),
  highFindings: z.number(),
  newSignals: z.number(),
  inTestingSignals: z.number(),
  needsReviewSignals: z.number(),
  testedSignals: z.number(),
  riskScore: z.number(),
});

export const DiscoveryTrendsSchema = z.object({
  date: z.string(),
  findings: z.number(),
  endpoints: z.number(),
  webPages: z.number(),
  signals: z.number(),
});

export const SignalGenerationTrendsSchema = z.object({
  date: z.string(),
  hot: z.number(),
  cold: z.number(),
  passed: z.number(),
  total: z.number(),
});

export const ApplicationGuardrailCreateSchema = ApplicationGuardrailSchema.omit(
  {
    id: true,
    tenantId: true,
    applicationId: true,
    createdAt: true,
    updatedAt: true,
  },
);

export const BaseMaliciousContentNotificationSchema = z.object({
  operationType: z.enum(['create', 'update']),
  applicationId: z.string(),
  itemId: z.string().optional(),
  detectedIssues: z.array(z.string()),
  originalContent: z.string(),
});

export const MaliciousContentNotificationSchema =
  BaseMaliciousContentNotificationSchema.extend({
    contentType: z.literal('guardrail'),
    fieldType: z.enum(['description', 'rule']),
    ruleIndex: z.number().optional(),
  });

export const TestingInstructionNotificationSchema =
  BaseMaliciousContentNotificationSchema.extend({
    contentType: z.literal('testing_instruction'),
  });

export const AnyMaliciousContentNotificationSchema = z.union([
  MaliciousContentNotificationSchema,
  TestingInstructionNotificationSchema,
]);

export const GuardrailValidationResponseSchema = z.object({
  success: z.boolean(),
  data: ApplicationGuardrailSchema.nullable(),
  validationErrors: z.array(GuardrailValidationErrorSchema).nullable(),
  message: z.string().optional(),
});

export const NoteValidationResponseSchema = z.object({
  success: z.boolean(),
  data: ApplicationNoteSchema.nullable(),
  validationErrors: z.array(NoteValidationErrorSchema).nullable(),
  message: z.string().optional(),
});

export const ApplicationGuardrailUpdateSchema =
  ApplicationGuardrailCreateSchema.partial();

export const ApplicationGuardrailsReturnSchema = z.object({
  guardrails: z.array(ApplicationGuardrailSchema),
});

const ApplicationNoteCreateBaseSchema = ApplicationNoteSchema.omit({
  id: true,
  tenantId: true,
  applicationId: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});

export const ApplicationNoteCreateSchema =
  ApplicationNoteCreateBaseSchema.superRefine((values, context) => {
    if (values.applyMode === 'AUTO_ATTACHED') {
      const hasGlob = values.endpointGlobs.some(
        (glob) => glob.trim().length > 0,
      );

      if (!hasGlob) {
        context.addIssue({
          code: 'custom',
          path: ['endpointGlobs'],
          message:
            'At least one endpoint glob is required for auto-attached mode',
        });
      }
    }

    if (values.applyMode === 'AGENT_REQUESTED') {
      if (!values.description?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['description'],
          message: 'Description is required for agent-requested mode',
        });
      }
    }
  });

export const ApplicationNoteUpdateSchema =
  ApplicationNoteCreateBaseSchema.partial();

export const ApplicationNoteWithUserSchema = ApplicationNoteSchema.extend({
  createdByUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
});

export const ApplicationNotesReturnSchema = z.object({
  notes: z.array(ApplicationNoteWithUserSchema),
});

export type ApplicationDashboardMetrics = z.infer<
  typeof ApplicationDashboardMetricsSchema
>;
export type MetricValue = z.infer<typeof MetricValueSchema>;
export type CountMetric = z.infer<typeof CountMetricSchema>;
export type ApplicationHealthMatrix = z.infer<
  typeof ApplicationHealthMatrixSchema
>;
export type DiscoveryTrends = z.infer<typeof DiscoveryTrendsSchema>;
export type SignalGenerationTrends = z.infer<
  typeof SignalGenerationTrendsSchema
>;
export type ApplicationAssetsCount = z.infer<
  typeof ApplicationAssetsCountSchema
>;
export type ApplicationUrlEditRestriction = z.infer<
  typeof ApplicationUrlEditRestrictionSchema
>;

export type GuardrailRule = z.infer<typeof GuardrailRuleSchema>;
export type GuardrailCategory = z.infer<typeof GuardrailCategorySchema>;
export type InstructionCategoryType = z.infer<typeof InstructionCategorySchema>;
export type GuardrailValidationError = z.infer<
  typeof GuardrailValidationErrorSchema
>;
export type BaseMaliciousContentNotification = z.infer<
  typeof BaseMaliciousContentNotificationSchema
>;
export type MaliciousContentNotification = z.infer<
  typeof MaliciousContentNotificationSchema
>;
export type TestingInstructionNotification = z.infer<
  typeof TestingInstructionNotificationSchema
>;
export type AnyMaliciousContentNotification = z.infer<
  typeof AnyMaliciousContentNotificationSchema
>;
export type GuardrailValidationResponse = z.infer<
  typeof GuardrailValidationResponseSchema
>;
export type NoteValidationError = z.infer<typeof NoteValidationErrorSchema>;
export type NoteValidationResponse = z.infer<
  typeof NoteValidationResponseSchema
>;
export type GuardrailAction = z.infer<typeof GuardrailActionSchema>;
export type GuardrailPipeline = z.infer<typeof GuardrailPipelineSchema>;
export type ApplicationGuardrail = z.infer<typeof ApplicationGuardrailSchema>;
export type ApplicationGuardrailCreate = z.infer<
  typeof ApplicationGuardrailCreateSchema
>;
export type ApplicationGuardrailUpdate = z.infer<
  typeof ApplicationGuardrailUpdateSchema
>;
export type ApplicationGuardrailsReturn = z.infer<
  typeof ApplicationGuardrailsReturnSchema
>;

export type ApplicationNote = z.infer<typeof ApplicationNoteSchema>;
export type ApplicationNoteCreate = z.infer<typeof ApplicationNoteCreateSchema>;
export type ApplicationNoteUpdate = z.infer<typeof ApplicationNoteUpdateSchema>;
export type ApplicationNotesReturn = z.infer<
  typeof ApplicationNotesReturnSchema
>;
export type ApplicationScopingReturn = z.infer<
  typeof ApplicationScopingReturnSchema
>;

export const TechnologyCveEndpointSchema = z.object({
  path: z.string().describe('Endpoint path'),
  codeGraphId: z
    .string()
    .nullable()
    .describe('Code graph ID associated with the endpoint'),
});

export const TechnologyWithCvesSchema = z.object({
  technology: z.string().describe('Technology name'),
  version: z.string().describe('Technology version range'),
  cveids: z
    .array(z.string())
    .describe('Array of CVE IDs affecting this technology'),
  endpoints: z
    .array(TechnologyCveEndpointSchema)
    .describe('Array of endpoints using this technology'),
});

export const TechnologiesWithCvesResponseSchema = z.object({
  totalTechnologies: z
    .number()
    .describe('Total number of unique technologies found'),
  topCves: z
    .array(TechnologyWithCvesSchema)
    .describe('Array of technologies with their CVEs and endpoints'),
});

export const ApplicationMetricsDetectionViewSchema = z
  .enum(['active', 'all'])
  .describe(
    'Filters findings and signals for card metrics. Active (default) counts only open detections; All includes historical statuses.',
  );

export const ApplicationMetricsIncludeSchema = z
  .enum([
    'riskScore',
    'findings',
    'signals',
    'assets',
    'entryPoints',
    'securityScore',
    'executions',
  ])
  .describe(
    'Optional metric groups to compute. When omitted, all metrics are returned. Unrequested keys are omitted from each application entry.',
  );

export const ApplicationsMetricsQuerySchema = z.object({
  detectionView: ApplicationMetricsDetectionViewSchema.optional(),
  include: z
    .array(ApplicationMetricsIncludeSchema)
    .optional()
    .describe(
      'When set, only the listed metric groups are computed and returned.',
    ),
  applicationIds: z
    .union([z.string().min(1), z.array(z.string().min(1))])
    .optional()
    .transform((value) => {
      if (value === undefined) {
        return undefined;
      }

      return Array.isArray(value) ? value : [value];
    })
    .describe(
      'Scope to specific application IDs. Omit for all in-scope applications.',
    ),
  environment: z.string().optional().describe('Filter by environment'),
  businessUnitId: z.string().optional().describe('Filter by business unit ID'),
  from: z
    .string()
    .optional()
    .describe(
      'Start of the metrics window (ISO). When set, counters exclude entities created before this time.',
    ),
  to: z
    .string()
    .optional()
    .describe(
      'End of the metrics window (ISO). When set, counters exclude entities created after this time.',
    ),
});

export const FindingsBySeveritySchema = z.object({
  critical: MetricValueSchema,
  high: MetricValueSchema,
  medium: MetricValueSchema,
  low: MetricValueSchema,
  info: MetricValueSchema,
});

export const FindingsByStatusSchema = z.object({
  unresolved: MetricValueSchema,
  inProgress: MetricValueSchema,
  retest: MetricValueSchema,
  resolved: MetricValueSchema,
  falsePositive: MetricValueSchema,
  wontFix: MetricValueSchema,
});

export const FindingsMetricSchema = z.object({
  total: MetricValueSchema.describe('Total findings count'),
  bySeverity: FindingsBySeveritySchema.optional(),
  byStatus: FindingsByStatusSchema.optional(),
});

export const ApplicationMetricsSchema = z.object({
  riskScore: MetricValueSchema.optional(),
  findings: FindingsMetricSchema.optional(),
  signals: CountMetricSchema.optional(),
  assets: CountMetricSchema.optional(),
  entryPoints: CountMetricSchema.optional(),
  securityScore: MetricValueSchema.optional(),
  executions: CountMetricSchema.optional(),
});

export const ApplicationsMetricsResponseSchema = z.record(
  z.string(),
  ApplicationMetricsSchema,
);

export type ApplicationMetricsDetectionView = z.infer<
  typeof ApplicationMetricsDetectionViewSchema
>;
export type ApplicationMetricsInclude = z.infer<
  typeof ApplicationMetricsIncludeSchema
>;
export type ApplicationsMetricsQuery = z.infer<
  typeof ApplicationsMetricsQuerySchema
>;
export type FindingsBySeverity = z.infer<typeof FindingsBySeveritySchema>;
export type FindingsByStatus = z.infer<typeof FindingsByStatusSchema>;
export type FindingsMetric = z.infer<typeof FindingsMetricSchema>;
export type ApplicationMetrics = z.infer<typeof ApplicationMetricsSchema>;
export type ApplicationsMetricsResponse = z.infer<
  typeof ApplicationsMetricsResponseSchema
>;

export const CrawlerSessionResponseSchema = GeneratedCrawlerSessionSchema.omit({
  updatedAt: true,
  createdAt: true, // API returns ISO string; base has z.coerce.date()
  tenantId: true, // Not returned in API; tenant is request context
  lastHeartbeatAt: true, // API returns ISO string
}).extend({
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  lastHeartbeatAt: z.string(),
});

export const CrawlerSessionStatsSchema = z.object({
  crawlerSessionId: z.string(),
  harEntriesNew: z.number(),
  harEntriesExisting: z.number(),
  endpointsNew: z.number(),
  endpointsExisting: z.number(),
  pagesNew: z.number(),
  pagesExisting: z.number(),
  webpagesNew: z.number(),
  webpagesExisting: z.number(),
  updatedAt: z.string(),
});

export const CrawlerLastFinishedMetaSchema = z.object({
  finishedAt: z.string(),
  crawlerType: CrawlerTypeSchema,
});

export const CrawlerLastFinishedByCredentialResponseSchema = z.object({
  lastFinishedByCredential: z.record(z.string(), CrawlerLastFinishedMetaSchema),
});

export const BulkAbortCrawlerSessionsBodySchema = z.object({
  applicationIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional list of application IDs to scope the abort. If omitted, aborts across all applications.',
    ),
  crawlerTypes: z
    .array(CrawlerTypeSchema)
    .optional()
    .describe(
      'Optional list of crawler types to abort (e.g., SHALLOW, AI). If omitted, aborts all types.',
    ),
});

export const BulkAbortCrawlerSessionsResponseSchema = z.object({
  success: z
    .boolean()
    .describe('True if abort was requested for at least one session'),
  message: z.string().describe('Status message'),
  abortedCount: z.number().describe('Number of sessions aborted'),
});

export const NotificationCenterMetricsSchema = z.object({
  newAssets: z.number(),
  newSignals: z.number(),
  attackSurfaceCode: z.number(),
  prsScanned: z.number(),
  newFindings: z.number(),
  findingsBySeverity: z.object({
    critical: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number(),
  }),
});

export const NotificationCenterMetricsQuerySchema = z.object({
  createdStartDate: z.coerce.date(),
});

export const APPLICATIONS_SETUP_COMPLETENESS_MAX_APPLICATION_IDS = 200;

export const ApplicationsSetupCompletenessDataBodySchema = z.object({
  applicationIds: z
    .array(z.string().min(1))
    .max(APPLICATIONS_SETUP_COMPLETENESS_MAX_APPLICATION_IDS)
    .describe('Application IDs to fetch setup completeness inputs for'),
});

export const ApplicationSetupCompletenessGitIntegrationSchema = z.object({
  repositories: z.array(
    z.object({
      id: z.union([z.string(), z.number()]).optional(),
    }),
  ),
});

export const ApplicationSetupCompletenessCredentialSchema = z.object({
  isDisabled: z.boolean(),
});

export const ApplicationSetupCompletenessDataItemSchema = z.object({
  applicationId: z.string().describe('Application ID'),
  gitIntegrations: z
    .array(ApplicationSetupCompletenessGitIntegrationSchema)
    .describe('Git integrations with repository stubs for completeness'),
  credentials: z
    .array(ApplicationSetupCompletenessCredentialSchema)
    .describe('Credential disabled flags for completeness'),
  applicationDocumentationFilesCount: z
    .int()
    .nonnegative()
    .describe('Count of application documentation files'),
  apiDocumentationFilesCount: z
    .int()
    .nonnegative()
    .describe('Count of API documentation files'),
  hasMappedKnowledgeSource: z
    .boolean()
    .describe(
      'Whether the application has at least one knowledge source mapping',
    ),
  isNetworkBound: z
    .boolean()
    .describe(
      'Whether an internal application has a persisted Twingate network binding',
    ),
});

export const ApplicationsSetupCompletenessDataResponseSchema = z.object({
  items: z.array(ApplicationSetupCompletenessDataItemSchema),
});

export type ApplicationsSetupCompletenessDataBody = z.infer<
  typeof ApplicationsSetupCompletenessDataBodySchema
>;
export type ApplicationSetupCompletenessDataItem = z.infer<
  typeof ApplicationSetupCompletenessDataItemSchema
>;
export type ApplicationsSetupCompletenessDataResponse = z.infer<
  typeof ApplicationsSetupCompletenessDataResponseSchema
>;

export const ApplicationProtoDerivedMethodSchema = z.object({
  methodPath: z.string(),
  service: z.string(),
  method: z.string(),
  requestType: z.string(),
  responseType: z.string(),
  requestStreaming: z.boolean(),
  responseStreaming: z.boolean(),
});

export const ApplicationProtoSchemaWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
  file: z.string().optional(),
});

export const ApplicationProtoSchemaBundleSchema = z.object({
  applicationId: z.string(),
  descriptorChecksum: z.string(),
  methods: z.array(ApplicationProtoDerivedMethodSchema),
  warnings: z.array(ApplicationProtoSchemaWarningSchema),
  compiledAt: z.string(),
  source: z.enum(['UPLOAD', 'REFLECTION', 'SOURCE_CODE']),
  sourceFileId: z.string().nullable(),
});

export const ApplicationProtoSchemaReturnSchema = z.object({
  bundle: ApplicationProtoSchemaBundleSchema.nullable(),
});

export const ApplicationProtoSchemaAutoDiscoverReturnSchema = z.object({
  message: z.string(),
  linkedRepositoryCount: z.number(),
});

export type ApplicationProtoDerivedMethod = z.infer<
  typeof ApplicationProtoDerivedMethodSchema
>;
export type ApplicationProtoSchemaWarning = z.infer<
  typeof ApplicationProtoSchemaWarningSchema
>;
export type ApplicationProtoSchemaBundle = z.infer<
  typeof ApplicationProtoSchemaBundleSchema
>;
export type ApplicationProtoSchemaReturn = z.infer<
  typeof ApplicationProtoSchemaReturnSchema
>;
export type ApplicationProtoSchemaAutoDiscoverReturn = z.infer<
  typeof ApplicationProtoSchemaAutoDiscoverReturnSchema
>;
